/**
 * Standalone SDK entry point.
 *
 * Built separately from the legacy bundle and deployed to `/sdk/bundle.js`.
 * Exposes `$epilot` on `window` — the fluent builder API for embedding
 * Journeys as either iframes or `<epilot-journey>` web components.
 *
 * ## Async install pattern
 *
 * The snippet recommended in the docs creates a tiny stub on `window`:
 *
 *   window.$epilot = { q: [], onReady(cb) { this.q.push(cb) } }
 *
 * When the real bundle loads, we detect the stub, replace `$epilot`
 * with the real {@link Epilot} instance, and drain the queued callbacks.
 *
 * Synchronous installers just get a ready `$epilot` immediately — they
 * never see the stub because the script blocks rendering.
 */
import { Epilot, type OnReadyCallback } from '@epilot/journey-embed-sdk'

import { getSdkOptionsFromEnv } from './utils/sdk-options'

type EpilotStub = {
  q?: OnReadyCallback[]
}

// The SDK internally honours `?debug=true` on the host page URL, so there's
// no need to wire `debug` through here.
const instance = new Epilot(getSdkOptionsFromEnv())

const stub = window.$epilot as unknown as EpilotStub | undefined
const queued = stub?.q

window.$epilot = instance

if (Array.isArray(queued)) {
  for (const cb of queued) {
    instance.onReady(cb)
  }
}
